import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class RouteValidationService {
  /**
   * Whitelist of allowed route patterns based on your actual routes
   * This is the MOST SECURE approach - only allow routes that exist
   */
  private readonly allowedRoutePatterns: RegExp[] = [
    // Patient overview modal routes (your email links)
    /^\/patient-overview-modal\/[a-zA-Z0-9_-]+$/,  // /patient-overview-modal/dc0001
    /^\/patient-overview-modal\/[a-zA-Z0-9_-]+\/exchange$/,  // /patient-overview-modal/dc0001/exchange
    /^\/patient-overview-modal\/[a-zA-Z0-9_-]+\/exchange\/[a-zA-Z0-9_-]+$/,  // /patient-overview-modal/dc0001/exchange/jordan-baker
    /^\/patient-overview-modal\/[a-zA-Z0-9_-]+\/exchange\/[a-zA-Z0-9_-]+\/images$/,  // .../images
    /^\/patient-overview-modal\/[a-zA-Z0-9_-]+\/exchange\/[a-zA-Z0-9_-]+\/images\/retreive$/,  // .../retreive
    
    // Image exchange routes (email links for image retrieval)
    /^\/image-exchange\/[a-zA-Z0-9_-]+\/from\/[a-zA-Z0-9_-]+\/to\/[a-zA-Z0-9_-]+\/images\/retreive$/,  // /image-exchange/patientId/from/sender/to/target/images/retreive
    
    // Patient overview routes
    /^\/patient-overview\/[a-zA-Z0-9_-]+$/,  // /patient-overview/dc0001
    /^\/patient-overview\/[a-zA-Z0-9_-]+\/.*$/,  // /patient-overview/dc0001/sender/... (any sub-paths)
    /^\/patient-overview$/,  // /patient-overview
    
    // Home page routes (protected)
    /^\/clients$/,  // /clients
    /^\/feedlibrary$/,  // /feedlibrary
    /^\/librarycategories$/,  // /librarycategories
    /^\/clientsmatters\/[a-zA-Z0-9_-]+$/,  // /clientsmatters/:type
    /^\/admins$/,  // /admins
    /^\/associates$/,  // /associates
    /^\/owners$/,  // /owners
    /^\/consultants$/,  // /consultants
    /^\/facilities$/,  // /facilities
    /^\/meetings$/,  // /meetings
    /^\/addsources$/,  // /addsources
    /^\/createclientmatter$/,  // /createclientmatter
    /^\/createadminuser$/,  // /createadminuser
    /^\/createclientsmatters$/,  // /createclientsmatters
    
    // Admin routes
    /^\/admin\/network$/,  // /admin/network
    /^\/admin\/storage-report$/,  // /admin/storage-report

    // Root/home
    /^\/$/,  // /
  ];

  constructor(private router: Router) {}

  /**
   * Validates if a URL is a safe internal route
   * Uses whitelist approach - only allows routes that match known patterns
   */
  isValidInternalRoute(url: string): boolean {
    if (!url || typeof url !== 'string') {
      console.warn('RouteValidationService: Invalid URL type:', typeof url);
      return false;
    }

    // Step 1: Remove query parameters and hash for validation
    // (We'll preserve them for the actual redirect, but validate the path)
    const path = url.split('?')[0].split('#')[0].trim();

    // Step 2: Must start with / (internal route)
    if (!path.startsWith('/')) {
      console.warn('RouteValidationService: URL does not start with /:', path);
      return false;
    }

    // Step 3: Reject external URLs (protocols)
    if (path.includes('://')) {
      console.warn('RouteValidationService: URL contains protocol:', path);
      return false;
    }

    // Step 4: Reject dangerous protocols (even if encoded)
    const dangerousPatterns = [
      /javascript:/i,
      /data:/i,
      /vbscript:/i,
      /file:/i,
      /%3a/i,  // encoded colon
      /%2f/i,  // encoded slash (could be used for path traversal)
    ];
    
    if (dangerousPatterns.some(pattern => pattern.test(path))) {
      console.warn('RouteValidationService: URL contains dangerous pattern:', path);
      return false;
    }

    // Step 5: Reject double slashes (could be protocol-relative URLs)
    if (path.startsWith('//')) {
      console.warn('RouteValidationService: URL starts with //:', path);
      return false;
    }

    // Step 6: Reject path traversal attempts
    if (path.includes('..') || path.includes('%2e%2e') || path.includes('%2E%2E')) {
      console.warn('RouteValidationService: URL contains path traversal:', path);
      return false;
    }

    // Step 7: Check against whitelist of allowed patterns
    const isAllowed = this.allowedRoutePatterns.some(pattern => pattern.test(path));
    
    if (!isAllowed) {
      console.warn('RouteValidationService: URL does not match any allowed pattern:', path);
    }
    
    return isAllowed;
  }

  /**
   * Additional validation: Check if route actually exists in Angular router
   * This provides a second layer of validation
   */
  async routeExists(url: string): Promise<boolean> {
    try {
      const path = url.split('?')[0].split('#')[0];
      // Try to match against router config
      // Angular router will handle 404s, but we can pre-validate
      return this.isValidInternalRoute(url);
    } catch (error) {
      console.error('RouteValidationService: Error checking route existence:', error);
      return false;
    }
  }

  /**
   * Sanitize and normalize a referer URL
   * Returns null if invalid, normalized URL if valid
   */
  sanitizeReferer(referer: string | null | undefined): string | null {
    if (!referer) {
      return null;
    }

    try {
      // Decode URL encoding
      let decoded = decodeURIComponent(referer);
      
      // Validate
      if (!this.isValidInternalRoute(decoded)) {
        return null;
      }
      
      return decoded;
    } catch (error) {
      console.warn('RouteValidationService: Failed to decode referer:', referer, error);
      return null;
    }
  }

  /**
   * Extracts patient ID from patient overview routes
   */
  extractPatientIdFromRoute(url: string): string | null {
    const match = url.match(/\/patient-overview-modal\/([a-zA-Z0-9_-]+)/);
    return match ? match[1] : null;
  }

  /**
   * Validates if a route is a patient overview route
   */
  isPatientOverviewRoute(url: string): boolean {
    return /^\/patient-overview-modal\/[a-zA-Z0-9_-]+/.test(url.split('?')[0]);
  }
}

